import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  ListChecks,
  FileText,
  FilePlus,
  CalendarPlus,
  Calendar,
  MailPlus,
  Briefcase,
  GitBranch,
  User,
  Clock,
  Scale,
  MessageCircle,
  ExternalLink,
  type LucideIcon
} from 'lucide-react';

/**
 * Action shape emitted by the backend `generateIntentActions()`.
 * `type` determines how the action is executed:
 *   - "navigate" → client-side route push
 *   - "prompt"   → inject text into the Lex input
 *   - "external"  → open URL in new tab
 *   - "editor.*" → dispatch custom DOM event for in-editor actions
 */
export interface LexAction {
  id: string;
  type: 'navigate' | 'prompt' | 'external' | string;
  title: string;
  icon?: string;
  route?: string;
  prompt?: string;
  url?: string;
  description?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  payload?: Record<string, any>;
}

// Icon map — matches backend `icon` strings to Lucide components
const ICON_MAP: Record<string, LucideIcon> = {
  'clipboard-list': ClipboardList,
  'list-checks': ListChecks,
  'file-text': FileText,
  'file-plus': FilePlus,
  'calendar-plus': CalendarPlus,
  'calendar': Calendar,
  'mail-plus': MailPlus,
  'briefcase': Briefcase,
  'git-branch': GitBranch,
  'user': User,
  'clock': Clock,
  'scale': Scale,
  'message-circle': MessageCircle,
  'external-link': ExternalLink,
};

interface LexActionCardProps {
  actions: unknown;
  onPromptAction?: (prompt: string) => void;
  messageId: string;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toLexAction(value: unknown, index: number): LexAction | null {
  if (!isPlainObject(value)) return null;

  const type = typeof value.type === 'string' ? value.type.trim() : '';
  if (!type) return null;

  const title = typeof value.title === 'string' && value.title.trim()
    ? value.title.trim()
    : type === 'navigate'
      ? 'Open'
      : type === 'prompt'
        ? 'Use Prompt'
        : type === 'external'
          ? 'Open Link'
          : 'Run Action';

  const variant = value.variant === 'primary' || value.variant === 'secondary' || value.variant === 'ghost'
    ? value.variant
    : 'secondary';

  return {
    id: typeof value.id === 'string' && value.id.trim() ? value.id.trim() : `action_${index}`,
    type,
    title,
    icon: typeof value.icon === 'string' ? value.icon : undefined,
    route: typeof value.route === 'string' ? value.route : undefined,
    prompt: typeof value.prompt === 'string' ? value.prompt : undefined,
    url: typeof value.url === 'string' ? value.url : undefined,
    description: typeof value.description === 'string' ? value.description : undefined,
    variant,
    payload: isPlainObject(value.payload) ? (value.payload as Record<string, any>) : undefined,
  };
}

function normalizeActions(input: unknown): LexAction[] {
  let candidates: unknown[] = [];

  if (Array.isArray(input)) {
    candidates = input;
  } else if (typeof input === 'string') {
    const trimmed = input.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          candidates = parsed;
        } else if (isPlainObject(parsed)) {
          const values = Object.values(parsed);
          candidates = values.some(Array.isArray)
            ? values.flatMap((entry) => Array.isArray(entry) ? entry : [entry])
            : [parsed];
        }
      } catch {
        candidates = [];
      }
    }
  } else if (isPlainObject(input)) {
    const values = Object.values(input);
    candidates = values.some(Array.isArray)
      ? values.flatMap((entry) => Array.isArray(entry) ? entry : [entry])
      : [input];
  }

  const normalized: LexAction[] = [];
  candidates.forEach((candidate, index) => {
    const action = toLexAction(candidate, index);
    if (action) {
      normalized.push(action);
    }
  });
  return normalized;
}

/**
 * LexActionCard — renders structured action buttons below an assistant message.
 * Supports navigation, prompt injection, and external links.
 */
export const LexActionCard: React.FC<LexActionCardProps> = ({
  actions,
  onPromptAction,
  messageId
}) => {
  const navigate = useNavigate();
  const normalizedActions = normalizeActions(actions);

  if (normalizedActions.length === 0) return null;

  const handleClick = (action: LexAction) => {
    switch (action.type) {
      case 'navigate':
        if (action.route) {
          navigate(action.route);
        }
        break;
      case 'prompt':
        if (action.prompt && onPromptAction) {
          onPromptAction(action.prompt);
        }
        break;
      case 'external':
        if (action.url) {
          window.open(action.url, '_blank', 'noopener,noreferrer');
        }
        break;
      default:
        // Editor or custom actions — dispatch DOM event
        if (action.type.startsWith('editor.')) {
          window.dispatchEvent(new CustomEvent('lex:action', { detail: { action } }));
        }
        break;
    }
  };

  const getVariantClasses = (variant: LexAction['variant'] = 'secondary') => {
    switch (variant) {
      case 'primary':
        return 'bg-lex-accent/10 text-lex-accent border-lex-accent/30 hover:bg-lex-accent/20 hover:border-lex-accent/50';
      case 'ghost':
        return 'bg-transparent text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300';
      case 'secondary':
      default:
        return 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 hover:border-gray-300 dark:hover:border-gray-600';
    }
  };

  // Separate primary actions from secondary/ghost
  const primaryActions = normalizedActions.filter(a => a.variant === 'primary');
  const otherActions = normalizedActions.filter(a => a.variant !== 'primary');

  return (
    <div className="flex flex-wrap gap-2 pt-2">
      {/* Primary actions rendered first with slightly more emphasis */}
      {primaryActions.map((action) => {
        const IconComponent = action.icon ? ICON_MAP[action.icon] : null;
        return (
          <button
            key={`${messageId}-action-${action.id}`}
            onClick={() => handleClick(action)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-150 ${getVariantClasses(action.variant)}`}
            title={action.description || action.title}
          >
            {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
            <span>{action.title}</span>
            {action.type === 'navigate' && <ExternalLink className="w-3 h-3 opacity-50" />}
          </button>
        );
      })}
      {/* Other actions */}
      {otherActions.map((action) => {
        const IconComponent = action.icon ? ICON_MAP[action.icon] : null;
        return (
          <button
            key={`${messageId}-action-${action.id}`}
            onClick={() => handleClick(action)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border transition-all duration-150 ${getVariantClasses(action.variant)}`}
            title={action.description || action.title}
          >
            {IconComponent && <IconComponent className="w-3 h-3" />}
            <span>{action.title}</span>
          </button>
        );
      })}
    </div>
  );
};

export default LexActionCard;
